// IntegrationsAPI.ts

import { getHeaders } from "@/contexts/AuthContext";

export interface Integration {
  id: string;
  integrationId: string;
  propertyId: string;
  type: string;
  name: string;
  icon: string;
}

export type AvailableIntegration = {
  id: string;
  name: string;
  type: string;
  url: string;
  callback: string;
  clientId: string;
  tokenUrl: string;
  secretName: string;
  logoIcon: string;
};

export interface IntegrationSecrets {
  clientId: string;
  clientSecret: string;
}

export interface XeroTenant {
  propertyId: string;
  integrationId: string;
  type: string;
  name: string;
  token: string;
  externalId: string;
}

export class IntegrationsAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "http:localhost:3001";
  }

  async savePropertyIntegration(
    propertyId: string,
    integrationId: string,
    type: string,
    name: string,
    token: string,
    externalId: string
  ): Promise<{ message: string }> {
    console.log("Saving property integration:", {
      propertyId,
      type,
      name,
      externalId,
    });
    const headers = await getHeaders();
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/integrations`,
        {
          mode: "cors",
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            propertyId,
            type,
            name,
            token,
            externalId,
            integrationId,
          }),
        }
      );
      console.log("response:", response);
      return { message: "Integration saved successfully" };
    } catch (error) {
      console.error("Error saving integration:", error);
      throw "Error saving integration";
    }
  }

  async deletePropertyIntegration(
    integrationId: string
  ): Promise<{ message: string }> {
    // Simulating API call with dummy data
    console.log("Deleting property integration:", integrationId);
    return { message: "Integration deleted successfully" };
  }

  async getIntegrationSecrets(secretName: string): Promise<IntegrationSecrets> {
    // Simulating API call with dummy data
    console.log("Getting secrets for:", secretName);

    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/secret?secretName=${secretName}`,
        {
          mode: "cors",
          method: "GET",
          headers: headers,
        }
      );
      const data = await response.json();

      if (!data.errors) {
        const secretObj = JSON.parse(data.secret);
        const clientId = Object.keys(secretObj)[0];
        const clientSecret = secretObj[Object.keys(secretObj)[0]];

        return {
          clientId: clientId,
          clientSecret: clientSecret,
        };
      }

      throw new Error("Failed to fetch secret");
    } catch (error) {
      console.error("Error fetching secret:", error);
      throw error;
    }
  }

  async getIntegration(integrationId: string): Promise<AvailableIntegration> {
    // Simulating API call with dummy data
    console.log("Getting integration");

    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/integration?id=${integrationId}`,
        {
          mode: "cors",
          method: "GET",
          headers: headers,
        }
      );
      const data = await response.json();
      console.log("data: ", data);
      if (!data.errors) {
        return data;
      }

      throw new Error("Failed to fetch integration");
    } catch (error) {
      console.error("Error fetching integration:", error);
      throw error;
    }
  }
}

export async function getPropertyIntegrations(
  propertyId: string
): Promise<Integration[]> {
  // Simulating API call with dummy data
  console.log("Getting property integrations for:", propertyId);
  try {
    const headers = await getHeaders();
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/integrations/${propertyId}`,
      {
        mode: "cors",
        method: "GET",
        headers: headers,
      }
    );
    const data = await response.json();

    console.log("integrations:", data);
    if (!data.errors) {
      //convert response into array of Integrations
      const integrations: Integration[] = data.map((item: any) => ({
        id: item.id,
        propertyId: item.propertyId,
        integrationId: item.integrationId,
        type: item.type,
        name: item.name,
        icon: item.icon || "", 
        externalId: item.externalId || "", 
        externalPropertyName: item.externalPropertyName || "" // Providing default empty string if icon is missing
      }));
      return integrations;
    }
  } catch (error) {
    console.error("Error:", error);
  }
}

export async function getToken(
  code: string,
  integrationId: string
): Promise<any> {
  //retrieve the integration
  const api = new IntegrationsAPI();
  const integration = await api.getIntegration(integrationId);
  const integrationSecret = await api.getIntegrationSecrets(
    integration.secretName
  );

  // Call OAuth2 token endpoint to exchange code for tokens
  try {
    const tokenResponse = await fetch(integration.tokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization:
          "Basic " +
          btoa(
            `${integrationSecret.clientId}:${integrationSecret.clientSecret}`
          ),
        "Access-Control-Allow-Origin": "*",
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        redirect_uri: integration.callback,
        client_id: integrationSecret.clientId,
        client_secret: integrationSecret.clientSecret,
      }),
    });

    if (!tokenResponse.ok) {
      throw new Error("Failed to get access token");
    }

    const tokenData = await tokenResponse.json();
    return tokenData;
  } catch (error) {
    console.error("Error getting access token:", error);
    throw error;
  }
}

export async function getTokenFromAPI(
  integrationId: string,
  code: string,
  propertyId: string
): Promise<any> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${import.meta.env.VITE_API_URL}/oauthToken`, {
      mode: "cors",
      method: "POST",
      headers: headers,

      body: JSON.stringify({
        integrationId: integrationId,
        code: code,
        propertyId: propertyId,
      }),
    });

    if (!response.ok) {
      console.log(response);
      throw new Error("Failed to retirieve token");
    }

    const tokenData = await response.json();
    console.log("tokenData: ", tokenData);
    return tokenData;
  } catch (error) {
    console.error("Error retrieving token:", error);
    throw error;
  }
}

//** this should no longer be required once all migrated to */
export async function saveToken(
  integrationName: string,
  propertyId: string,
  integrationId: string,
  token: string
): Promise<boolean> {
  try {
    const headers = await getHeaders();
    const response = await fetch(`${import.meta.env.VITE_API_URL}/secret`, {
      mode: "cors",
      method: "POST",
      headers: headers,

      body: JSON.stringify({
        secretName: `${integrationName}-${propertyId}-${integrationId}`,
        secretValue: token,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save token");
    }
    console.log("Token saved successfully");
    return true;
  } catch (error) {
    console.error("Error saving token:", error);
    throw error;
  }
}

export async function getCloudbedsHotels(
  propertyId: string,
  integrationId: string
): Promise<any> {
  try {
    const headers = await getHeaders();
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/cloudbeds/hotels`,
      {
        mode: "cors",
        method: "POST",
        headers: headers,

        body: JSON.stringify({
          propertyId: propertyId,
          integrationId: integrationId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to retrieve Hotels");
    }

    const hotels = await response.json();
    return hotels;
  } catch (error) {
    console.error("Error retrieving hotels:", error);
    throw error;
  }
}

export async function saveXeroTenant(tenant: XeroTenant): Promise<boolean> {
  const api = new IntegrationsAPI();
  try {
    await api.savePropertyIntegration(
      tenant.propertyId,
      tenant.integrationId,
      tenant.type,
      tenant.name,
      tenant.token,
      tenant.externalId
    );
    return true;
  } catch (error) {
    console.error("Error saving Xero tenant:", error);
    throw "Error saving Xero tenant";
  }
}

export async function getAvailableIntegrations(
  integrationType: string,
  propertyId: string
): Promise<AvailableIntegration[]> {
  console.log("Getting available integrations");

  try {
    const headers = await getHeaders();
    const response = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/AvailableIntegrations?type=${integrationType}&propertyId=${propertyId}`,
      {
        mode: "cors",
        method: "GET",
        headers: headers,
      }
    );
    const data = await response.json();

    if (!data.errors) {
      console.log("Available integrations: ", data);
      return data;
    }

    throw new Error("Failed to fetch available integrations");
  } catch (error) {
    console.error("Error fetching available integrations:", error);
    throw error;
  }
}

export async function deleteIntegration(
  id: string
): Promise<{ message: string }> {
  try {
    const headers = await getHeaders();
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/integrations`,
      {
        mode: "cors",
        method: "DELETE",
        headers: headers,
        body: JSON.stringify({
          id,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete integration");
    }

    const result = await response.json();
    console.log("Integration deleted successfully:", result);
    return { message: "Integration deleted successfully" };
  } catch (error) {
    console.error("Error deleting integration:", error);
    throw error;
  }
}
